import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import Plotter, { PlotterContent } from '../../../components/Plotter';
import Skeleton from '../../../components/Skeleton';

interface RunPlotterContext {
  run?: any;
  isRunsPending: boolean;
  isRunsFetching: boolean;
  calibration?: any;
}

interface BodyPosConfig {
  mode: 'delta' | 'absolute';
  showReference: boolean;
  head: boolean;
  chest: boolean;
  waist: boolean;
}

const BODYPOS_CONFIG_KEY = 'run-plotter-bodypos-config';

const DEFAULT_BODYPOS_CONFIG: BodyPosConfig = {
  mode: 'delta',
  showReference: false,
  head: true,
  chest: true,
  waist: false,
};

function getBodyPosConfig(): BodyPosConfig {
  try {
    const stored = localStorage.getItem(BODYPOS_CONFIG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.mode === 'string' && typeof parsed.head === 'boolean') {
        return { ...DEFAULT_BODYPOS_CONFIG, ...parsed };
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_BODYPOS_CONFIG;
}

export default function RunPlotter(): ReactElement {
  const { run, isRunsPending, calibration } = useOutletContext<RunPlotterContext>();

  const [bodyPosConfig, setBodyPosConfig] = useState<BodyPosConfig>(getBodyPosConfig);

  // Persist config to localStorage
  useEffect(() => {
    localStorage.setItem(BODYPOS_CONFIG_KEY, JSON.stringify(bodyPosConfig));
  }, [bodyPosConfig]);

  const updateConfig = useCallback((patch: Partial<BodyPosConfig>) => {
    setBodyPosConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  // Build available fields based on mode and body part selection
  const bodyPosFields = useMemo(() => {
    const fields: { label: string; field: string }[] = [];
    const { mode, showReference, head, chest, waist } = bodyPosConfig;
    const isDelta = mode === 'delta';

    if (head) {
      fields.push(
        isDelta
          ? { label: 'Head Delta', field: 'BodyPosHeadDelta_sG21_cm' }
          : { label: 'Head', field: 'BodyPosHead_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Head Reference', field: 'BodyPosRefHead_sG11_cm' });
      }
    }
    if (chest) {
      fields.push(
        isDelta
          ? { label: 'Chest Delta', field: 'BodyPosChestDelta_sG21_cm' }
          : { label: 'Chest', field: 'BodyPosChest_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Chest Reference', field: 'BodyPosRefChest_sG11_cm' });
      }
    }
    if (waist) {
      fields.push(
        isDelta
          ? { label: 'Waist Delta', field: 'BodyPosWaistDelta_sG21_cm' }
          : { label: 'Waist', field: 'BodyPosWaist_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Waist Reference', field: 'BodyPosRefWaist_sG11_cm' });
      }
    }
    return fields;
  }, [bodyPosConfig]);

  const speedFields = [
    { label: 'Road Speed', field: 'RoadSpeed' },
    { label: 'Air Speed', field: 'AirSpeed' },
  ];

  const speedFieldsSecondary = [{ label: 'Rider Power', field: 'RiderPwr_P' }];

  const baroFields = [
    { label: 'Barometric Pressure Raw', field: 'Baro_pG21' },
    { label: 'Barometric Pressure', field: 'Baro_pG31' },
  ];

  const chipStyle = 'cr-flex cr-items-center cr-justify-center cr-text-center cr-cursor-pointer cr-select-none';

  return (
    <div className="cr-flex cr-flex-col cr-gap-4">
      <Plotter
        isPending={isRunsPending}
        availableFields={speedFields}
        data={run?.data_gridded || null}
        title="Road and Air Speed vs Time"
        storageKey="run-plotter-speed-selections"
        yAxisTitle="Speed (km/h)"
        yAxisUnit="km/h"
        availableFieldsSecondary={speedFieldsSecondary}
        yAxisTitleRight="Power (W)"
        yAxisUnitRight="W"
        calibration={calibration}
        segmentTimestamps={run?.segment_ts}
        excludeCandidates={run?.exclude_candidates_ts}
        excludeTs={run?.exclude_ts}
        runId={run?.id}
      />

      <Plotter
        isPending={isRunsPending}
        availableFields={baroFields}
        data={run?.data_gridded || null}
        title="Barometric Pressure vs Time"
        storageKey="run-plotter-baro-selections"
        yAxisTitle="Pressure (Pa)"
        yAxisUnit="Pa"
        calibration={calibration}
      />

      {/* Body Position Controls — rendered inside the Plotter's card via title area */}
      <div className="cr-space-y-6">
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold">Body Position vs Time</h2>

          {/* Top Row: Delta/Absolute radio + Show Reference checkbox */}
          <div className="cr-flex cr-items-center cr-gap-6 cr-mb-3">
            <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
              <input
                type="radio"
                name="bodypos-mode"
                checked={bodyPosConfig.mode === 'delta'}
                onChange={() => updateConfig({ mode: 'delta' })}
                className="cr-h-4 cr-w-4 cr-text-blue-600"
              />
              <span className="cr-text-sm cr-font-medium">Delta</span>
            </label>
            <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
              <input
                type="radio"
                name="bodypos-mode"
                checked={bodyPosConfig.mode === 'absolute'}
                onChange={() => updateConfig({ mode: 'absolute' })}
                className="cr-h-4 cr-w-4 cr-text-blue-600"
              />
              <span className="cr-text-sm cr-font-medium">Absolute</span>
            </label>
            {bodyPosConfig.mode === 'absolute' && (
              <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer cr-ml-4">
                <input
                  type="checkbox"
                  checked={bodyPosConfig.showReference}
                  onChange={() => updateConfig({ showReference: !bodyPosConfig.showReference })}
                  className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-blue-600"
                />
                <span className="cr-text-sm cr-font-medium">Show Reference</span>
              </label>
            )}
          </div>

          {/* Lower Row: Head / Chest / Waist chip buttons */}
          <div className="cr-flex cr-gap-2 cr-mb-2">
            <div
              className={`${chipStyle} ${bodyPosConfig.head ? 'chip-active' : 'chip-inactive'}`}
              onClick={() => updateConfig({ head: !bodyPosConfig.head })}
            >
              Head
            </div>
            <div
              className={`${chipStyle} ${bodyPosConfig.chest ? 'chip-active' : 'chip-inactive'}`}
              onClick={() => updateConfig({ chest: !bodyPosConfig.chest })}
            >
              Chest
            </div>
            <div
              className={`${chipStyle} ${bodyPosConfig.waist ? 'chip-active' : 'chip-inactive'}`}
              onClick={() => updateConfig({ waist: !bodyPosConfig.waist })}
            >
              Waist
            </div>
          </div>

          {/* Chart (no title, no ChipSelector — controls are above) */}
          <Skeleton isPending={isRunsPending}>
            {bodyPosFields.length > 0 ? (
              <PlotterContent
                availableFields={bodyPosFields}
                data={run?.data_gridded || null}
                storageKey="run-plotter-bodypos-selections"
                yAxisTitle="Body Position (cm)"
                yAxisUnit="cm"
                hideChipSelector
                calibration={calibration}
              />
            ) : (
              <div className="cr-mt-4 cr-text-gray-500">
                Select at least one body part (Head, Chest, or Waist) to display data.
              </div>
            )}
          </Skeleton>
        </div>
      </div>
    </div>
  );
}
